import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

interface Stats {
  totalInstitutions: number;
  totalCities: number;
  totalConsultations: number;
  completedConsultations: number;
}

export default function CtaSection() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const { count: institutionCount } = await supabase
        .from('institutions').select('*', { count: 'exact', head: true }).eq('approval_status', 'approved');
      const { data: cityData } = await supabase
        .from('institutions').select('city').eq('approval_status', 'approved');
      const uniqueCities = cityData ? new Set(cityData.map((item) => item.city).filter(Boolean)).size : 0;
      const { count: consultationCount } = await supabase
        .from('consultations').select('*', { count: 'exact', head: true });
      const { count: completedCount } = await supabase
        .from('consultations').select('*', { count: 'exact', head: true }).eq('status', 'completed');
      setStats({ totalInstitutions: institutionCount || 0, totalCities: uniqueCities, totalConsultations: consultationCount || 0, completedConsultations: completedCount || 0 });
    } catch {
      setStats({ totalInstitutions: 0, totalCities: 0, totalConsultations: 0, completedConsultations: 0 });
    } finally { setLoading(false); }
  };

  const completionRate = stats && stats.totalConsultations > 0
    ? Math.round((stats.completedConsultations / stats.totalConsultations) * 100) : 0;

  const statItems = [
    { value: stats?.totalInstitutions || 0, labelKey: 'cta_stat_centers', icon: 'ri-hospital-line' },
    { value: stats?.totalConsultations || 0, labelKey: 'cta_stat_consultations', icon: 'ri-chat-3-line' },
    { value: `%${completionRate}`, labelKey: 'cta_stat_completed', icon: 'ri-checkbox-circle-line' },
    { value: stats?.totalCities || 0, labelKey: 'cta_stat_cities', icon: 'ri-map-pin-line' },
  ];

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/12c73806f413bcac434f9e481eed8b9a.jpg"
          alt="Health Institution"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2C3E3A]/90 to-[#2C3E3A]/80"></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block px-4 py-1.5 bg-rose-500/20 text-rose-300 text-xs font-semibold rounded-full mb-6 uppercase tracking-wider border border-rose-400/30">
              {t('cta_badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-sans leading-tight">
              {t('cta_title')}
              <br />
              <span className="text-rose-300">{t('cta_title_highlight')}</span>
            </h2>
            <p className="text-base text-white/70 mb-8 leading-relaxed">{t('cta_subtitle')}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/kurum-paneli"
                className="inline-flex items-center justify-center px-7 py-3.5 bg-rose-500 text-white rounded-xl font-semibold hover:bg-rose-600 transition-all text-sm whitespace-nowrap shadow-lg shadow-rose-500/25 cursor-pointer"
              >
                <i className="ri-hospital-line mr-2"></i>
                {t('cta_register')}
              </Link>
              <Link
                to="/iletisim"
                className="inline-flex items-center justify-center px-7 py-3.5 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all text-sm whitespace-nowrap cursor-pointer"
              >
                {t('cta_more_info')}
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {loading ? (
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="p-5 bg-white/10 backdrop-blur-sm rounded-xl border border-white/10 animate-pulse">
                  <div className="w-10 h-10 bg-white/20 rounded-lg mb-3"></div>
                  <div className="h-7 bg-white/20 rounded w-16 mb-2"></div>
                  <div className="h-3 bg-white/20 rounded w-20"></div>
                </div>
              ))
            ) : statItems.map((stat) => (
              <div key={stat.labelKey} className="p-5 bg-white/10 backdrop-blur-sm rounded-xl border border-white/10 hover:bg-white/15 transition-all">
                <div className="w-10 h-10 flex items-center justify-center bg-rose-500/20 rounded-lg mb-3">
                  <i className={`${stat.icon} text-rose-300 text-lg`}></i>
                </div>
                <p className="text-2xl font-bold text-white font-sans">{stat.value}</p>
                <p className="text-xs text-white/60 mt-1">{t(stat.labelKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
